import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { notification, Modal, theme, Button } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useFilters from 'utils/hooks/useFilters';
import { IEntityMaintainingRegistry } from 'store/ducks/DataEntry/Entities/types';
import CustomTable from 'components/CustomTable/CustomTable';
import useCountries from 'utils/hooks/fetchHooks/useCountries';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { RootState } from 'store/configureStore';
import { set_isSubmitionTrue } from 'store/ducks/DataEntry/Entities/entitySlice';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getEntityMaintainingColumns } from 'components/Columns/EntityMaintainingTableColumns';
import SaveButton from 'components/SaveButton/SaveButton';
import { globalOp } from 'store/ducks/Global';
import { useTranslation } from 'react-i18next';
import * as Styled from '../DataEntry.styled';
import ApiService from 'utils/ApiCalls';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const EntityMaintainingregistry = () => {
  const { query } = useFilters();
  dayjs.extend(customParseFormat);
  const { confirm } = Modal;
  const dispatch = useDispatch();
  const { useToken } = theme;
  const { token } = useToken();
  const { t } = useTranslation();

  const [data, setData] = useState<IEntityMaintainingRegistry[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [initialData, setInitialData] = useState<IEntityMaintainingRegistry[]>(
    [],
  );
  const [tableName, setTableName] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [changedData, setChangedData] = useState<Record<string, any>[]>([]);
  const { countries } = useCountries();
  const [entityTypes, setEntityTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const [competentAuthorities, setCompetentAuthorities] = useState<
    { label: string; value: string }[]
  >([]);
  const { idOfSelectedFinancialEntity } = useSelector(
    (state: RootState) => state.entities,
  );
  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const fetchFinancialEntitiesById = async () => {
    try {
      if (idOfSelectedFinancialEntity === null) return;
      const response = await entityOp.fetchFinancialEntitiesById(
        idOfSelectedFinancialEntity,
      );
      const data = response?.data || [];
      const mappedData = [
        {
          key: 0,
          isNew: false,
          isEdited: false,
          id: data.financial_entity_id || 0,
          lei_entity_maintaining_register_information:
            data.lei_entity_maintaining_register_information || '',
          financial_entity_id: data.financial_entity_id || 0,
          financial_entity_name: data.financial_entity_name || '',
          country_id: data.country_id || '',
          entity_type_id: data.entity_type_id || '',
          competent_authority_id: data.competent_authority_id || '',
          reporting_date: data.reporting_date || '',
        },
      ];
      setData(mappedData);
      setInitialData(mappedData);
    } catch (error) {
      setData([]);
    }
  };

  const handleSave = (row: IEntityMaintainingRegistry) => {
    const isRowInDataSource = data.some((item) => item.key === row.key);

    if (isRowInDataSource) {
      const updatedDataSource = data.map((item) =>
        item.key === row.key ? { ...item, ...row } : item,
      );
      setData(updatedDataSource);
    }
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = data.filter((item) => item.key !== key);
        setData(updatedDataSource);
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 10,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 10,
        });
      },
    });
  };

  const handleInactivate = (record: IEntityMaintainingRegistry) => {
    if (!record.financial_entity_id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: record.financial_entity_id,
      tableName: 'financial_entity_lei',
      onSuccess: fetchFinancialEntitiesById,
    });
  };

  const fetchEntityType = async () => {
    try {
      const response = await entityOp.fetchEntityTypes();
      if (response?.data.entity_types) {
        const formattedOptions = response.data.entity_types.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setEntityTypes(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCompetentAuthorities = async () => {
    try {
      const response = await entityOp.fetchCompetentAuthorities();
      if (response?.data.competent_authorities) {
        const formattedOptions = response.data.competent_authorities.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setCompetentAuthorities(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (idOfSelectedFinancialEntity) {
      fetchFinancialEntitiesById();
    }
    fetchEntityType();
    fetchCompetentAuthorities();
  }, [idOfSelectedFinancialEntity]);

  const addNewRow = () => {
    const newRowTemplate: IEntityMaintainingRegistry = {
      key: generateUniqueId(),
      id: generateUniqueId(),
      lei_entity_maintaining_register_information: '',
      financial_entity_name: '',
      country_id: '',
      entity_type_id: '',
      competent_authority_id: '',
      reporting_date: '',
      isNew: true,
    };
    const newRow = structuredClone(newRowTemplate);

    setData((prevData) => [...prevData, newRow]);
    setChangedData((prevChangedData) => [...prevChangedData, newRow]);
  };

  const handleSubmitEntity = async () => {
    if (JSON.stringify(data) === JSON.stringify(initialData)) {
      notification.info({
        message: 'Info',
        description: 'No data has been changed.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const hasEmptyFields = data.some((item) =>
      Object.values(item).some((value) => value === '' || value === null),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please fill in all the fields with yellow background before submission.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    try {
      const leis = data.map(
        (item) => item.lei_entity_maintaining_register_information,
      );
      const validationResults = await globalOp.validateLEI(leis);
      Object.entries(validationResults).forEach(
        ([lei, result]: [string, any]) => {
          if (result.valid) {
            notification.success({
              message: lei,
              description: result.details,
              duration: 5,
            });
          } else {
            notification.error({
              message: lei,
              description: result.details,
              duration: 5,
            });
          }
        },
      );

      const allValid = Object.values(validationResults).map(
        (r: any) => r.valid === true,
      );

      if (allValid.every(Boolean)) {
        const entityData = {
          data_list: data,
          entity_name: 'Financial Entities',
        };

        const response = await entityOp.fetchFinancialEntity(entityData);

        if (response?.status === 200) {
          fetchFinancialEntitiesById();
          dispatch(set_isSubmitionTrue(true));
        }
      } else {
        console.warn('One or more LEIs are invalid');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const columns = getEntityMaintainingColumns(
    handleSave,
    handleRemoveRow,
    handleInactivate,
    addNewRow,
    countries,
    entityTypes,
    competentAuthorities,
    t,
  );

  return (
    <div>
      <Styled.HighlightedTable>
        <CustomTable
          columns={columns}
          dataSource={data}
          handleSave={handleSave}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            onChange: handlePageChange,
            showSizeChanger: true,
          }}
          locale={{
            emptyText: (
              <Button type="primary" onClick={() => addNewRow()}>
                {t('GENERAL.ADD_FIRST_ROW')}
              </Button>
            ),
          }}
        />
      </Styled.HighlightedTable>
      <Styled.ButtonRow>
        {data.length > 0 && (
          <Styled.AreaButton
            token={token}
            type="primary"
            onClick={() => addNewRow()}
          >
            Add new row
          </Styled.AreaButton>
        )}
        <SaveButton
          token={token}
          hasChanges={JSON.stringify(data) !== JSON.stringify(initialData)}
          onClick={handleSubmitEntity}
        />
      </Styled.ButtonRow>
    </div>
  );
};

export default EntityMaintainingregistry;
